import { modTypes } from "@blings/blings-player";
import { updateObjectValues } from "../../../helpers/JsonSearch";
import type { IModModel } from "../../../stores/ModsStore";
import type { TimelineLayerInfo } from "./layerHierarchy";
import { normalizeKeyframePath } from "./keyframeHelpers";
import { modTargetsLayer } from "./timingModHelpers";

type KeyframeBezierHandle = {
  x: number | number[];
  y: number | number[];
};

type KeyframeLike = {
  h?: 0 | 1;
  i?: KeyframeBezierHandle;
  o?: KeyframeBezierHandle;
  s?: number[];
  e?: number[];
  t: number;
};

type AnimatedPropertyLike = {
  a?: number;
  k: unknown;
  [key: string]: unknown;
};

const DEFAULT_LINEAR_HANDLE = {
  in: { x: 0.667, y: 0.667 },
  out: { x: 0.333, y: 0.333 },
} as const;

function isStructuredValue(
  value: unknown
): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === "object";
}

function doesPathContainTarget(path: string, targetPath: string): boolean {
  return targetPath.startsWith(`${path}.`);
}

export function getValueAtObjectPath(source: unknown, path: string): unknown {
  if (!path) return undefined;

  return normalizeKeyframePath(path)
    .split(".")
    .filter(Boolean)
    .reduce<unknown>((current, segment) => {
      if (current == null || typeof current !== "object") {
        return undefined;
      }

      if (!(segment in (current as Record<string, unknown>))) {
        return undefined;
      }

      return (current as Record<string, unknown>)[segment];
    }, source);
}

function cloneArrayValue(value: number[]): number[] {
  return [...value];
}

function cloneHandle(
  handle: KeyframeBezierHandle | typeof DEFAULT_LINEAR_HANDLE.in
): KeyframeBezierHandle {
  return {
    x: Array.isArray(handle.x) ? [...handle.x] : handle.x,
    y: Array.isArray(handle.y) ? [...handle.y] : handle.y,
  };
}

function cloneKeyframe(keyframe: KeyframeLike): KeyframeLike {
  return {
    ...keyframe,
    ...(keyframe.s ? { s: cloneArrayValue(keyframe.s) } : {}),
    ...(keyframe.e ? { e: cloneArrayValue(keyframe.e) } : {}),
    ...(keyframe.i ? { i: cloneHandle(keyframe.i) } : {}),
    ...(keyframe.o ? { o: cloneHandle(keyframe.o) } : {}),
  };
}

function isAnimatedPropertyValue(
  value: unknown
): value is AnimatedPropertyLike {
  return (
    value !== null &&
    typeof value === "object" &&
    "a" in value &&
    (value as AnimatedPropertyLike).a === 1 &&
    Array.isArray((value as AnimatedPropertyLike).k)
  );
}

function fromArrayValue(value: number[]): number | number[] {
  return value.length === 1 ? value[0] : value;
}

function sortAndDeduplicateKeyframes(
  keyframes: KeyframeLike[]
): KeyframeLike[] {
  const sorted = keyframes
    .map((keyframe) => cloneKeyframe(keyframe))
    .sort((left, right) => left.t - right.t);
  const deduped: KeyframeLike[] = [];

  sorted.forEach((keyframe) => {
    const previous = deduped[deduped.length - 1];
    if (previous && previous.t === keyframe.t) {
      deduped[deduped.length - 1] = keyframe;
      return;
    }

    deduped.push(keyframe);
  });

  return deduped;
}

function normalizeKeyframeConnections(
  keyframes: KeyframeLike[]
): KeyframeLike[] {
  return sortAndDeduplicateKeyframes(keyframes).map((keyframe, index, list) => {
    const normalized = cloneKeyframe(keyframe);
    const next = list[index + 1];

    if (!next || normalized.h === 1) {
      delete normalized.e;
      delete normalized.i;
      delete normalized.o;
      return normalized;
    }

    normalized.e = cloneArrayValue(next.s ?? normalized.s ?? []);
    normalized.i = cloneHandle(normalized.i ?? DEFAULT_LINEAR_HANDLE.in);
    normalized.o = cloneHandle(normalized.o ?? DEFAULT_LINEAR_HANDLE.out);

    return normalized;
  });
}

function getAnimatedPropertyValueAtFrame(
  property: AnimatedPropertyLike,
  frame: number
): number | number[] {
  const keyframes = (property.k as KeyframeLike[])
    .map((keyframe) => cloneKeyframe(keyframe))
    .sort((left, right) => left.t - right.t);

  if (!keyframes.length) {
    return 0;
  }

  const first = keyframes[0];
  if (frame <= first.t) {
    return fromArrayValue(cloneArrayValue(first.s ?? []));
  }

  for (let index = 0; index < keyframes.length; index += 1) {
    const current = keyframes[index];
    const next = keyframes[index + 1];

    if (!next || frame <= next.t) {
      const startValue = cloneArrayValue(current.s ?? []);
      const endValue = cloneArrayValue(current.e ?? next?.s ?? current.s ?? []);
      const duration = Math.max(1, (next?.t ?? current.t) - current.t);
      const progress =
        current.h === 1
          ? 0
          : Math.min(1, Math.max(0, (frame - current.t) / duration));

      return fromArrayValue(
        startValue.map((value, componentIndex) => {
          const endComponent = endValue[componentIndex] ?? value;
          return value + (endComponent - value) * progress;
        })
      );
    }
  }

  return fromArrayValue(
    cloneArrayValue(keyframes[keyframes.length - 1].s ?? [])
  );
}

export function collapseAnimatedPropertiesToStaticValue(
  value: unknown,
  frame: number
): unknown {
  if (isAnimatedPropertyValue(value)) {
    return {
      ...value,
      a: 0,
      k: getAnimatedPropertyValueAtFrame(value, frame),
    };
  }

  if (Array.isArray(value)) {
    return value.map((entry) =>
      collapseAnimatedPropertiesToStaticValue(entry, frame)
    );
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        collapseAnimatedPropertiesToStaticValue(entry, frame),
      ])
    );
  }

  return value;
}

export function removeKeyframeFromAnimatedPropertyValue(
  value: unknown,
  frame: number
): { removed: boolean; value: unknown } {
  if (!isAnimatedPropertyValue(value)) {
    return { removed: false, value };
  }

  const existingKeyframes = value.k as KeyframeLike[];
  const remainingKeyframes = existingKeyframes.filter(
    (keyframe) => keyframe.t !== frame
  );

  if (remainingKeyframes.length === existingKeyframes.length) {
    return { removed: false, value };
  }

  if (remainingKeyframes.length < 2) {
    const fallbackValue =
      remainingKeyframes[0]?.s != null
        ? fromArrayValue(cloneArrayValue(remainingKeyframes[0].s))
        : getAnimatedPropertyValueAtFrame(value, frame);

    return {
      removed: true,
      value: {
        ...value,
        a: 0,
        k: fallbackValue,
      },
    };
  }

  return {
    removed: true,
    value: {
      ...value,
      a: 1,
      k: normalizeKeyframeConnections(remainingKeyframes),
    },
  };
}

export function findExactCustomPathMod(
  mods: IModModel[],
  layer: TimelineLayerInfo,
  targetPath: string
): IModModel | undefined {
  const normalizedTargetPath = normalizeKeyframePath(targetPath);

  return mods.find(
    (mod) =>
      mod?.moddata?.type === modTypes.custom &&
      !mod.moddata.isDisabled &&
      typeof mod.moddata.path === "string" &&
      modTargetsLayer(mod, layer) &&
      normalizeKeyframePath(mod.moddata.path) === normalizedTargetPath
  );
}

export function findDescendantCustomPathMods(
  mods: IModModel[],
  layer: TimelineLayerInfo,
  rootPath: string
): IModModel[] {
  const normalizedRootPath = normalizeKeyframePath(rootPath);

  return mods.filter((mod) => {
    if (mod?.moddata?.type !== modTypes.custom) return false;
    if (mod.moddata.isDisabled) return false;
    if (typeof mod.moddata.path !== "string") return false;
    if (!modTargetsLayer(mod, layer)) return false;

    const normalizedPath = normalizeKeyframePath(mod.moddata.path);
    return doesPathContainTarget(normalizedRootPath, normalizedPath);
  });
}

export function findNearestAncestorAnimationMod(
  mods: IModModel[],
  layer: TimelineLayerInfo,
  targetPath: string
): IModModel | undefined {
  const normalizedTargetPath = normalizeKeyframePath(targetPath);

  return mods
    .filter((mod) => {
      if (mod?.moddata?.type !== modTypes.custom) return false;
      if (mod.moddata.isDisabled) return false;
      if (typeof mod.moddata.path !== "string") return false;
      if (!isStructuredValue(mod.moddata.value)) return false;
      if (!modTargetsLayer(mod, layer)) return false;

      const normalizedModPath = normalizeKeyframePath(mod.moddata.path);
      if (normalizedModPath === normalizedTargetPath) return false;

      return doesPathContainTarget(normalizedModPath, normalizedTargetPath);
    })
    .sort((left, right) => {
      const leftDepth = normalizeKeyframePath(left.moddata.path!).split(".")
        .length;
      const rightDepth = normalizeKeyframePath(right.moddata.path!).split(".")
        .length;
      return rightDepth - leftDepth;
    })[0];
}

export function buildUpdatedAncestorAnimationValue(
  mod: IModModel,
  targetPath: string,
  nextValue: unknown
): Record<string, unknown> | unknown[] {
  const normalizedModPath = normalizeKeyframePath(mod.moddata.path!);
  const normalizedTargetPath = normalizeKeyframePath(targetPath);
  const relativePath = normalizedTargetPath.slice(normalizedModPath.length + 1);
  const nextRootValue = JSON.parse(JSON.stringify(mod.moddata.value)) as
    | Record<string, unknown>
    | unknown[];

  updateObjectValues(
    nextRootValue as Record<string, unknown>,
    [relativePath],
    [nextValue as any]
  );

  return nextRootValue;
}
